import type { MaskRuleConfig } from "../contracts/types";
import type { AnonymizationResult } from "../domain/engine";
import type { RunState } from "../hooks/useEngine";
import { CoverageMeter } from "./CoverageMeter";
import { FieldRulesTable } from "./FieldRulesTable";
import { FindingsList } from "./FindingsList";
import { DiffPreview } from "./DiffPreview";
import { ExportPanel } from "./ExportPanel";

interface ResultPanelProps {
  runState: RunState;
  engineResult: AnonymizationResult | null;
  onChangeRule: (field: string, patch: Partial<Pick<MaskRuleConfig, "strategy" | "param">>) => void;
}

const STATUS_LABELS: Record<string, string> = {
  completed: "Ejecución terminada",
  partial: "Ejecución parcial",
  failed: "No se pudo completar",
  cancelled: "Cancelada por el visitante",
};

export function ResultPanel({ runState, engineResult, onChangeRule }: ResultPanelProps) {
  return (
    <section aria-labelledby="result-heading">
      <h2 id="result-heading" className="section-heading">
        Resultado y explicación
      </h2>
      <div className="panel" aria-live="polite">
        {runState === "idle" && !engineResult ? (
          <p className="empty-state">Aún no hay resultado. Ejecuta el fixture para ver cada decisión.</p>
        ) : null}

        {runState === "processing" ? <p>Procesando el dataset en un Web Worker… puedes cancelar en cualquier momento.</p> : null}

        {engineResult ? (
          <div>
            <p>
              <span className={`state-badge state-badge--${runState === "failed" ? "critical" : runState === "cancelled" ? "warning" : "success"}`}>
                {STATUS_LABELS[engineResult.output.status] ?? engineResult.output.status}
              </span>
            </p>
            <p>{engineResult.output.summary}</p>

            <h3>Cobertura</h3>
            <CoverageMeter coverage={engineResult.coverage} />

            <h3>Hallazgos</h3>
            <FindingsList findings={engineResult.output.findings} />

            {engineResult.fieldProfiles.length > 0 ? (
              <>
                <h3>Campos y reglas de máscara</h3>
                <p className="field-hint">
                  Cambia una regla para ver el resultado distinto de inmediato (mismo dataset, otra decisión).
                </p>
                <FieldRulesTable
                  fieldProfiles={engineResult.fieldProfiles}
                  effectiveRules={engineResult.effectiveRules}
                  perFieldCoverage={engineResult.coverage.perField}
                  onChangeRule={onChangeRule}
                />

                <h3>Vista previa / diff</h3>
                <DiffPreview diff={engineResult.diff} fieldOrder={engineResult.fieldOrder} />

                <ExportPanel result={engineResult} />
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
